/**
 * PDFErrorHandler component for PDF loading failures
 * Requirements: 1.4, 3.4
 */

import React from 'react';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import RefreshIcon from '@mui/icons-material/Refresh';
import DescriptionIcon from '@mui/icons-material/Description';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

interface PDFErrorHandlerProps {
  error: Error | string;
  documentUrl?: string;
  onRetry?: () => void;
  onReportIssue?: (error: Error | string) => void;
}

/**
 * Specialized error handler for PDF loading and rendering failures
 * Provides user-friendly error messages and recovery options specific to PDF issues
 */
export function PDFErrorHandler({ 
  error, 
  documentUrl, 
  onRetry, 
  onReportIssue 
}: PDFErrorHandlerProps) {
  const errorMessage = typeof error === 'string' ? error : error.message;
  
  const getPDFErrorDetails = (errorMsg: string) => {
    // Common PDF.js error patterns and user-friendly messages
    const errorPatterns = [
      {
        pattern: /Invalid PDF structure|PDF header not found/i,
        title: 'Invalid PDF File',
        message: 'The document appears to be corrupted or is not a valid PDF file.',
        suggestion: 'Please verify the document and try uploading it again.',
        severity: 'error' as const,
      },
      {
        pattern: /Password required|Encrypted PDF/i,
        title: 'Password Protected Document',
        message: 'This PDF document is password protected and cannot be displayed.',
        suggestion: 'Please provide an unprotected version of the document.',
        severity: 'warning' as const,
      },
      {
        pattern: /Network error|Failed to fetch/i,
        title: 'Network Connection Error',
        message: 'Unable to download the PDF document due to a network issue.',
        suggestion: 'Please check your internet connection and try again.',
        severity: 'error' as const,
      },
      {
        pattern: /Loading task destroyed|AbortError/i,
        title: 'Loading Interrupted',
        message: 'PDF loading was interrupted or cancelled.',
        suggestion: 'Please try loading the document again.',
        severity: 'warning' as const,
      },
      {
        pattern: /UnexpectedResponseException|HTTP error/i,
        title: 'Server Error',
        message: 'The server encountered an error while providing the PDF document.',
        suggestion: 'Please try again later or contact support if the issue persists.',
        severity: 'error' as const,
      },
      {
        pattern: /Out of memory|Memory limit exceeded/i,
        title: 'Document Too Large',
        message: 'The PDF document is too large to display in your browser.',
        suggestion: 'Try using a smaller document or contact support for assistance.',
        severity: 'warning' as const,
      },
    ];

    const matchedPattern = errorPatterns.find(pattern => 
      pattern.pattern.test(errorMsg)
    );

    return matchedPattern || {
      title: 'PDF Loading Error',
      message: 'An unexpected error occurred while loading the PDF document.',
      suggestion: 'Please try again or contact support if the problem continues.',
      severity: 'error' as const,
    };
  };

  const errorDetails = getPDFErrorDetails(errorMessage);

  const handleRetry = () => {
    onRetry?.();
  };

  const handleReportIssue = () => {
    onReportIssue?.(error);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 400,
        p: 3,
        textAlign: 'center',
      }}
    >
      <Alert
        severity={errorDetails.severity}
        icon={<ErrorOutlineIcon />}
        sx={{ width: '100%', maxWidth: 500, mb: 2 }}
      >
        <AlertTitle>{errorDetails.title}</AlertTitle>
        
        <Typography variant="body1" sx={{ mb: 1 }}>
          {errorDetails.message}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {errorDetails.suggestion}
        </Typography>

        <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 2 }}>
          {onRetry && (
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={handleRetry}
              size="small"
            >
              Try Again
            </Button>
          )}
          
          {onReportIssue && (
            <Button
              variant="outlined"
              startIcon={<DescriptionIcon />}
              onClick={handleReportIssue}
              size="small"
            >
              Report Issue
            </Button>
          )}
        </Stack>
      </Alert>

      {/* Technical details for development/debugging */}
      {process.env.NODE_ENV === 'development' && (
        <Box sx={{ mt: 3, width: '100%', maxWidth: 600 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Technical Details:
          </Typography>
          <Box
            component="pre"
            sx={{
              fontSize: '0.75rem',
              backgroundColor: 'grey.100',
              p: 2,
              borderRadius: 1,
              overflow: 'auto',
              textAlign: 'left',
              border: 1,
              borderColor: 'grey.300',
            }}
          >
            Error: {errorMessage}
            {documentUrl && `\nDocument URL: ${documentUrl}`}
            {typeof error === 'object' && error.stack && `\nStack: ${error.stack}`}
          </Box>
        </Box>
      )}
    </Box>
  );
}

/**
 * Hook for handling PDF errors with automatic retry logic
 */
export function usePDFErrorHandler() {
  const [error, setError] = React.useState<Error | string | null>(null);
  const [retryCount, setRetryCount] = React.useState(0);
  const maxRetries = 3;

  const handleError = React.useCallback((error: Error | string) => {
    console.error('PDF Error:', error);
    setError(error);
  }, []);

  const retry = React.useCallback(() => {
    if (retryCount < maxRetries) {
      setError(null);
      setRetryCount(prev => prev + 1);
      return true;
    }
    return false;
  }, [retryCount, maxRetries]);

  const reset = React.useCallback(() => {
    setError(null);
    setRetryCount(0);
  }, []);

  const reportIssue = React.useCallback((error: Error | string) => {
    // Log error for reporting
    const errorReport = {
      error: typeof error === 'string' ? error : error.message,
      stack: typeof error === 'object' ? error.stack : undefined,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      retryCount,
    };

    console.log('PDF Error Report:', errorReport);
    
    // In a real application, send to error reporting service
    // Example: Send to Azure Application Insights
    // if (window.appInsights) {
    //   window.appInsights.trackException({ 
    //     exception: typeof error === 'object' ? error : new Error(error),
    //     properties: errorReport 
    //   });
    // }
  }, [retryCount]);

  return {
    error,
    retryCount,
    canRetry: retryCount < maxRetries,
    handleError,
    retry,
    reset,
    reportIssue,
  };
}

export default PDFErrorHandler;